/**
 * Merge AI-generated sections with existing notes
 * - Detects Markdown headers (## Section Name)
 * - Replaces existing sections
 * - Adds new sections at the end
 */
export function mergeNoteSections(
  existingNotes: string,
  generatedSections: Record<string, string>
): string {
  let updatedNotes = existingNotes || '';

  // Mapping of section IDs to Markdown headers
  const sectionHeaders: Record<string, string> = {
      system_information: '## System Information',
      management_summary: '## Management Summary',
      scope: '## Systems in/out of Scope',
      auth_concept: '## Authentication & Authorization Concept',
  };

  for (const [sectionId, content] of Object.entries(generatedSections)) {
    const header = sectionHeaders[sectionId];
    if (!header) continue;

    // Regex to find section (header + content until next header or EOF)
      const title = header.replace(/^#+\s*/, '');
      // Regex to match the header (any level) + content until next header or EOF
      // Use case-insensitive and multiline flags, avoid global to safely test/replace
      const sectionRegex = new RegExp(`#{1,6}\\s*${escapeRegex(title)}[\\s\\S]*?(?=\\n#{1,6}\\s*[^\n]+|$)`, 'im');

    // Ensure the generated content is a string and includes the canonical header.
    let safeContent = typeof content === 'string' ? content : '';
    // If AI returned its own header (any level) that matches this section title, strip it
    // e.g. '# System Information' or '## System Information' at the start of content
      const headerPattern = new RegExp(`^#{1,6}\\s*${escapeRegex(header.replace(/^#+\\s*/, ''))}\\s*\\n`, 'i');
    if (headerPattern.test(safeContent)) {
      safeContent = safeContent.replace(headerPattern, '').trimStart();
    }

    const normalizedContent = `${header}\n\n${safeContent}`;

    if (sectionRegex.test(updatedNotes)) {
      // Replace existing section with normalized content
      updatedNotes = updatedNotes.replace(sectionRegex, normalizedContent);
    } else {
      // Add new section at the end with header and canonical separator
      const SEPARATOR = '\n\n---\n\n';
      if (updatedNotes.trim() === '') {
        updatedNotes = `${normalizedContent}`;
      } else {
        // Ensure we add a separator between the last content and the new section
        if (updatedNotes.endsWith('\n\n') || updatedNotes.endsWith('\n\n---\n\n')) {
          updatedNotes += `${normalizedContent}`;
        } else {
          updatedNotes += `${SEPARATOR}${normalizedContent}`;
        }
      }
    }
  }

  // Normalize repeated separators
  updatedNotes = updatedNotes.replace(/(\n\n---\n\n)+/g, '\n\n---\n\n');

  return updatedNotes.trim();
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
